import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';
import { Editor } from 'primereact/editor';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';

import { DropdownOption } from '../../../models/common/DropdownOption';
import { IManageRemindersResponse } from '../../../models/manageReminders/manageRemindersModels';
import { HttpService, IHttpService } from '../../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../../services/notifierService';

import './ManageRemindersForm.scss';
import { IManageRemindersFormProps } from './IManageRemindersFormProps';
import { useCookies } from 'react-cookie';

const ManageRemindersForm = ({ type }: IManageRemindersFormProps) => {
	const [divisionsOptions, setDivisionsOptions] = useState<DropdownOption[]>(
		[]
	);

	const [cookies, setCookie] = useCookies(['remindersDivision']);

	const parseCookieValue = (cookieValue: string) => {
		const parsedValue = parseInt(cookieValue);
		const endValue = isNaN(parsedValue) ? 1 : parsedValue;
		return endValue;
	};

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [shouldReload, setShouldReload] = useState<boolean>(false);
	const [isPrevQuarter, setIsPrevQuarter] = useState<boolean>(false);
	const [{ year, quarter }, setYearQuarter] = useState<{
		year: number;
		quarter: number;
	}>(getQuarterAndYear(isPrevQuarter));
	const [selectedDivisionId, setSelectedDivisionId] = useState<number>(
		parseCookieValue(cookies.remindersDivision)
	);
	const [reminderData, setReminderData] = useState<IManageRemindersResponse>({
		id: 0,
		notificationType: 0,
		name: '',
		isActive: true,
		isSent: false,
		divisionId: 0,
		sendDate: new Date(),
		minSendDate: new Date(),
		maxSendDate: new Date(),
		subject: '',
		body: ''
	});
	const [body, setBody] = useState<string>('');

	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	const fetchDivisions = async () => {
		httpService
			.get<DropdownOption[]>('divisions/dropdown', undefined)
			.then((options) => {
				setDivisionsOptions(options);
			})
			.catch((err) => notifierService.error());
	};

	const fetchReminderData = async () => {
		httpService
			.get<IManageRemindersResponse>(
				`reminders?divisionId=${selectedDivisionId}&quarter=${quarter}&year=${year}&NotificationType=${type}`,
				undefined
			)
			.then((reminderDataRes) => {
				setReminderData((prevReminderData) => ({
					id: reminderDataRes.id,
					notificationType: reminderDataRes.notificationType,
					name: reminderDataRes.name,
					isActive: reminderDataRes.isActive,
					isSent: reminderDataRes.isSent,
					divisionId: reminderDataRes.divisionId,
					sendDate: new Date(reminderDataRes.sendDate as any),
					minSendDate: new Date(reminderDataRes.minSendDate as any),
					maxSendDate: new Date(reminderDataRes.maxSendDate as any),
					subject: reminderDataRes.subject || '',
					body: reminderDataRes.body || ''
				}));
				setBody(reminderDataRes.body);
			})
			.catch((err) => notifierService.error());
	};

	function getQuarterAndYear(isPrevQuarter: boolean) {
		const today = new Date();
		let calcYear = today.getFullYear();
		const currentMonth = today.getMonth() + 1;
		let calcQuarter = 1;

		if (currentMonth >= 4 && currentMonth <= 6) {
			calcQuarter = 2;
		} else if (currentMonth >= 7 && currentMonth <= 9) {
			calcQuarter = 3;
		} else if (currentMonth >= 10 && currentMonth <= 12) {
			calcQuarter = 4;
		}

		if (isPrevQuarter) {
			calcQuarter--;

			if (calcQuarter === 0) {
				calcYear--;
				calcQuarter = 4;
			}
		}

		return {
			year: calcYear,
			quarter: calcQuarter
		};
	}

	useEffect(() => {
		const fetchData = async () => {
			await fetchDivisions();
			await fetchReminderData();
		};

		fetchData();
	}, [selectedDivisionId, isPrevQuarter]);

	useEffect(() => {
		if (shouldReload) {
			fetchReminderData();
			setShouldReload(false);
		}
	}, [shouldReload, fetchReminderData]);

	const handleSaveReminder = async (e: any): Promise<void> => {
		e.preventDefault();

		if (body.trim() === '' || reminderData.subject.trim() === '') {
			notifierService.info(`Reminder body/subject cannot be left blank.`);
			return;
		}

		setIsLoading(true);

		try {
			if (reminderData.id === 0) {
				await httpService.post(
					`reminders`,
					JSON.stringify({
						...reminderData,
						year,
						quarter,
						body,
						sendDate: convertDateToUtc(reminderData.sendDate),
						maxSendDate: convertDateToUtc(reminderData.maxSendDate),
						minSendDate: convertDateToUtc(reminderData.minSendDate)
					}),
					undefined,
					false
				);
			} else {
				await httpService.put(
					`reminders`,
					JSON.stringify({
						...reminderData,
						body,
						sendDate: convertDateToUtc(reminderData.sendDate),
						maxSendDate: convertDateToUtc(reminderData.maxSendDate),
						minSendDate: convertDateToUtc(reminderData.minSendDate)
					}),
					undefined,
					false
				);
			}

			setShouldReload((x) => !x);

			notifierService.info(
				`${reminderData.name} reminder has been saved.`
			);
		} catch (err) {
			notifierService.error(err);
		} finally {
			setIsLoading(false);
		}
	};

	const handleToggleQuarter = (e: any) => {
		e.preventDefault();
		setIsPrevQuarter((prevValue) => !prevValue);
		setYearQuarter(getQuarterAndYear(!isPrevQuarter));
	};

	const handleSetActiveReminder = async (e: any): Promise<void> => {
		e.preventDefault();
		setIsLoading(true);

		try {
			await httpService.put(
				`reminders/${reminderData.id}/changeActive`,
				undefined,
				undefined,
				false
			);

			setShouldReload(true);

			notifierService.info(
				`${reminderData.name} reminder has been ${
					reminderData.isActive ? 'disabled' : 'enabled'
				}.`
			);
		} catch (err) {
			notifierService.error(err);
		} finally {
			setIsLoading(false);
		}
	};

	function convertDateToUtc(date: Date) {
		//removes the timezone component
		return new Date(
			Date.UTC(
				date.getFullYear(),
				date.getMonth(),
				date.getDate(),
				date.getHours(),
				date.getMinutes()
			)
		);
	}

	return (
		<form
			className={`manage-reminders-form ${
				reminderData.isSent || isLoading || !reminderData.isActive
					? 'disabled'
					: ''
			}`}
		>
			<div className='manage-reminders-form__row'>
				<h5 className='manage-reminders-form__quarter-info'>
					{isPrevQuarter ? (
						<>
							previous quarter:{' '}
							<strong>
								(Q{quarter}, {year})
							</strong>
						</>
					) : (
						<>
							current quarter{' '}
							<strong>
								(Q{quarter}, {year})
							</strong>
						</>
					)}
				</h5>
			</div>

			<div className='manage-reminders-form__row'>
				<div className='manage-reminders-form__calendar'>
					<label htmlFor='date'>Date</label>
					<Calendar
						id='date'
						dateFormat='dd/mm/yy'
						minDate={reminderData.minSendDate}
						maxDate={reminderData.maxSendDate}
						readOnlyInput
						value={reminderData.sendDate}
						onChange={(e) =>
							setReminderData({
								...reminderData,
								sendDate: e.value as any
							})
						}
						disabled={
							isLoading ||
							reminderData.isSent ||
							!reminderData.isActive
						}
					/>
				</div>

				<div className='manage-reminders-form__division'>
					<label htmlFor='division'>Division</label>
					<Dropdown
						id='division'
						value={selectedDivisionId}
						options={divisionsOptions}
						onChange={(e: DropdownChangeParams) => {
							setSelectedDivisionId(e.target.value);
							setCookie('remindersDivision', e.value);
						}}
					/>
				</div>
			</div>

			<div className='manage-reminders-form__row'>
				<div className='manage-reminders-form__subject'>
					<label htmlFor='subject'>Subject</label>
					<InputText
						id='subject'
						value={reminderData.subject}
						disabled={
							isLoading ||
							reminderData.isSent ||
							!reminderData.isActive
						}
						onChange={(e) =>
							setReminderData({
								...reminderData,
								subject: e.target.value
							})
						}
					/>
				</div>
			</div>

			<div className='manage-reminders-form__editor'>
				<label htmlFor='body'>Message</label>
				<Editor
					id='body'
					value={body}
					onTextChange={(e) => setBody(e.htmlValue || '')}
					readOnly={
						isLoading ||
						reminderData.isSent ||
						!reminderData.isActive
					}
					disabled={
						isLoading ||
						reminderData.isSent ||
						!reminderData.isActive
					}
				/>
			</div>

			<div className='manage-reminders-form__bottom'>
				<div className='manage-reminders-form__bottom-btns-group'>
					<button
						className='btn primary'
						disabled={
							isLoading ||
							reminderData.isSent ||
							!reminderData.isActive
						}
						onClick={handleSaveReminder}
					>
						{reminderData.id == 0 ? 'Save' : 'Update'}
					</button>

					{reminderData.id != 0 && (
						<button
							className='btn secondary'
							disabled={isLoading}
							onClick={handleSetActiveReminder}
						>
							{reminderData.isActive ? 'Disable' : 'Enable'}
						</button>
					)}
				</div>

				<button
					className='btn tertiary'
					disabled={isLoading}
					onClick={handleToggleQuarter}
				>
					{isPrevQuarter ? '' : '◄- '} Show{' '}
					{isPrevQuarter ? 'Current' : 'Previous'} Quarter{' '}
					{isPrevQuarter ? ' -►' : ''}
				</button>
			</div>
		</form>
	);
};

export default ManageRemindersForm;
